import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useEditor, EditorContent } from '@tiptap/react';
import { asHtml } from './richTextHtml';
import StarterKit from '@tiptap/starter-kit';
import './RichText.css';
import { useConfirm } from '../../../hooks/useConfirm';

// Editable rich-text field. Mirrors the Client Wins RichTextEditor: exposes
// getHTML()/isEmpty() imperatively so callers can read live content at save
// time without waiting for React state to flush.
export const RichTextEditor = forwardRef(({ value, onChange, disabled }, ref) => {
    const [dialogs, dialogUi] = useConfirm();
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const editor = useEditor({
        extensions: [StarterKit],
        content: asHtml(value),
        editable: !disabled,
        immediatelyRender: false,
    });

    useImperativeHandle(ref, () => ({
        getHTML: () => editor?.getHTML() ?? '',
        isEmpty: () => editor?.isEmpty ?? true,
    }), [editor]);

    useEffect(() => {
        if (!editor) return undefined;
        const handler = ({ editor: ed }) => onChangeRef.current?.(ed.getHTML());
        editor.on('update', handler);
        return () => { editor.off('update', handler); };
    }, [editor]);

    useEffect(() => {
        if (editor) editor.setEditable(!disabled);
    }, [editor, disabled]);

    // tiptap only reads `content` at creation, so external resets are pushed in
    // manually — guarding against echoing the user's own typing back in.
    useEffect(() => {
        if (!editor) return;
        const next = asHtml(value);
        if (editor.getHTML() === next) return;
        editor.commands.setContent(next, { emitUpdate: false });
    }, [editor, value]);

    // Links are prompted for rather than parsed out of the text: the outreach
    // blocks in the Integration Hub are full of credential and guide URLs, and
    // a wrong one is worse than none.
    const setLink = async () => {
        if (!editor) return;
        const previous = editor.getAttributes('link')?.href ?? '';
        const input = await dialogs.prompt({ title: 'Link', label: 'Link URL', defaultValue: previous, confirmLabel: 'Set link', message: 'Leave it empty to remove the link.' });
        if (input === null) return;
        const url = input.trim();
        if (!url) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        const href = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    };

    return (
        <Box className="rte-wrapper">
            {dialogUi}
            <Box className="rte-toolbar">
                {[
                    { icon: <FormatBoldIcon sx={{ fontSize: 16 }} />, cmd: 'bold', action: () => editor?.chain().focus().toggleBold().run() },
                    { icon: <FormatItalicIcon sx={{ fontSize: 16 }} />, cmd: 'italic', action: () => editor?.chain().focus().toggleItalic().run() },
                    { icon: <FormatListBulletedIcon sx={{ fontSize: 16 }} />, cmd: 'bulletList', action: () => editor?.chain().focus().toggleBulletList().run() },
                    { icon: <FormatListNumberedIcon sx={{ fontSize: 16 }} />, cmd: 'orderedList', action: () => editor?.chain().focus().toggleOrderedList().run() },
                    { icon: <LinkIcon sx={{ fontSize: 16 }} />, cmd: 'link', action: setLink },
                    {
                        icon: <LinkOffIcon sx={{ fontSize: 16 }} />,
                        cmd: 'unlink',
                        action: () => editor?.chain().focus().extendMarkRange('link').unsetLink().run(),
                        // Only meaningful with the caret inside a link.
                        inactive: !editor?.isActive('link'),
                    },
                ].map(({ icon, cmd, action, inactive }) => (
                    <IconButton
                        key={cmd}
                        size="small"
                        onMouseDown={(e) => { e.preventDefault(); action(); }}
                        className={editor?.isActive(cmd) ? 'rte-btn-active' : ''}
                        disabled={disabled || Boolean(inactive)}
                    >
                        {icon}
                    </IconButton>
                ))}
            </Box>
            <EditorContent editor={editor} className="rte-content" />
        </Box>
    );
});

// Read-only render of stored HTML. Uses a non-editable tiptap instance (no
// dangerouslySetInnerHTML) so lists/bold/italic render exactly as authored.
export const RichTextView = ({ value }) => {
    const editor = useEditor({
        extensions: [StarterKit],
        content: asHtml(value),
        editable: false,
        immediatelyRender: false,
    });

    useEffect(() => {
        if (!editor) return;
        const next = asHtml(value);
        if (editor.getHTML() === next) return;
        editor.commands.setContent(next, { emitUpdate: false });
    }, [editor, value]);

    const empty = !value || value === '<p></p>';
    if (empty) {
        return <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>—</Typography>;
    }
    return (
        <Box className="rte-view">
            <EditorContent editor={editor} className="rte-content" />
        </Box>
    );
};
